import React, { useState, useRef } from 'react';
import { PDFDocument, PDFName, PDFString, PDFDict, PDFArray } from 'pdf-lib';
import { Upload, Download, Loader2, FileCheck } from 'lucide-react';

const PdfReplacer: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [newUrl, setNewUrl] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error', msg: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setStatus(null);
    }
  };

  const processPdf = async () => {
    if (!file || !newUrl) {
      setStatus({ type: 'error', msg: 'Please provide both a PDF and a new URL.' });
      return;
    }

    setIsProcessing(true);
    setStatus(null);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pages = pdfDoc.getPages();

      let linkCount = 0;

      for (const page of pages) {
        const annotsRaw = page.node.get(PDFName.of('Annots'));
        const annots = pdfDoc.context.lookup(annotsRaw);

        if (annots instanceof PDFArray) {
          for (let i = 0; i < annots.size(); i++) {
            const annot = pdfDoc.context.lookup(annots.get(i));
            
            if (annot instanceof PDFDict) {
              const subtype = annot.get(PDFName.of('Subtype'));
              
              if (subtype === PDFName.of('Link')) {
                const actionRaw = annot.get(PDFName.of('A'));
                const action = pdfDoc.context.lookup(actionRaw);
                
                if (action instanceof PDFDict) {
                  const s = action.get(PDFName.of('S'));
                  if (s === PDFName.of('URI')) {
                    action.set(PDFName.of('URI'), PDFString.of(newUrl));
                    linkCount++;
                  }
                }
              }
            }
          }
        }
      }

      if (linkCount === 0) {
        setStatus({ 
          type: 'error', 
          msg: 'Processed, but 0 links were found to replace.' 
        });
        setIsProcessing(false);
        return;
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `goldfinch_processed_${file.name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setStatus({ type: 'success', msg: `Successfully replaced ${linkCount} link(s).` });
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', msg: 'Error processing PDF.' });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-8 max-w-2xl mx-auto shadow-sm">
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2 text-zinc-900">PDF Link Replacer</h2>
        <p className="text-zinc-500 text-sm">
          Upload a document and specify a new URL. This tool will automatically find every clickable link inside the PDF and redirect it to your new destination.
        </p>
      </div>

      <div className="space-y-6">
        <div 
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-10 flex flex-col items-center justify-center cursor-pointer transition-colors ${
            file ? 'border-[#D4AF37] bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300 bg-zinc-50/50'
          }`}
        >
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            ref={fileInputRef}
            className="hidden"
          />
          {file ? (
            <>
              <FileCheck className="text-[#D4AF37] w-10 h-10 mb-2" />
              <span className="text-sm font-medium text-zinc-900">{file.name}</span>
              <span className="text-xs text-zinc-400 mt-1">Click to change file</span>
            </>
          ) : (
            <>
              <Upload className="text-zinc-400 w-10 h-10 mb-2" />
              <span className="text-sm text-zinc-500">Click or drag PDF here</span>
              <span className="text-xs text-zinc-400 mt-1">Supports standard PDF files</span>
            </>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-400">New Destination URL</label>
          <input
            type="url"
            placeholder="https://goldfinchexecutive.com/destination"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            className="w-full bg-zinc-50 border border-zinc-200 rounded-lg py-3 px-4 text-zinc-900 focus:outline-none focus:border-[#D4AF37] transition-colors"
          />
        </div>

        <button
          onClick={processPdf}
          disabled={isProcessing || !file || !newUrl}
          className="w-full bg-[#D4AF37] text-black font-bold py-4 rounded-lg flex items-center justify-center gap-2 hover:bg-[#C19A2E] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
        >
          {isProcessing ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Analysing & Updating...
            </>
          ) : (
            <>
              <Download size={20} />
              Process & Download PDF
            </>
          )}
        </button>

        {status && (
          <div className={`p-4 rounded-lg text-sm text-center ${
            status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {status.msg}
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfReplacer;